<template>
  <div class="equipment-page">
    <TravelPlannerHero
      :showInfo="false"
      :heading="$t('travelPlanner.equipmentPage.equipmentHeader')"
    />
    <section class="equipment-page grid-layout page-wrapper">
      <div class="grid-layout__main">
        <TabContainer
          :hasTwoContents="hasInboundProducts"
          alignment="left"
          :tabOneText="$t('booking.outbound')"
          :tabTwoText="$t('booking.inbound')"
          v-model="activeTab"
        >
          <template #content1>
            <Equipment
              v-for="(leg, index) in outboundLegs"
              :key="leg.id"
              :products="outboundProducts[index]?.products || []"
              :is-loading="isLoading"
              :leg="{ ...leg, direction: 'outbound' }"
              direction="outbound"
              :selected-outbound-products="selectedOutboundProducts"
              :selected-inbound-products="selectedInboundProducts"
              @add-product="(product) => addProduct(product, 'outbound')"
              @remove-product="(product) => removeProduct(product, 'outbound')"
            />
          </template>
          <template #content2>
            <Equipment
              v-for="(leg, index) in inboundLegs"
              :key="leg.id"
              :products="inboundProducts[index]?.products || []"
              :is-loading="isLoading"
              :leg="{ ...leg, direction: 'inbound' }"
              direction="inbound"
              :selected-outbound-products="selectedOutboundProducts"
              :selected-inbound-products="selectedInboundProducts"
              @add-product="(product) => addProduct(product, 'inbound')"
              @remove-product="(product) => removeProduct(product, 'inbound')"
            />
          </template>
        </TabContainer>
      </div>
      <div class="grid-layout__sidebar">
        <MyTravel
          v-if="!isMobileView"
          :origin="origin"
          :destination="destination"
          :hasReturnTravel="hasReturnTravel"
          :isRebooking="isRebooking"
          :showTotalPrice="false"
        />
      </div>
      <div class="grid-layout__main">
        <NavigationBar :isColumn="true">
          <div class="navigation-bar__upper">
            <PriceTag v-if="!isRebooking" :isRebooking="myTravelStore.isInRebookingMode" />
            <button
              class="navigation-bar__details-button button button--reset"
              @click="toggleDrawer"
              v-if="isMobileView"
            >
              {{ $t('searchResult.navigation.showDetails') }}
              <svg class="trailing__chevron">
                <use :xlink:href="`#${chevron}`" />
              </svg>
            </button>
          </div>
          <div class="navigation-bar__lower">
            <button
              class="button button--medium button--secondary"
              @click="handleBack"
            >
              {{ $t('common.actions.back') }}
            </button>
            <button
              class="button button--medium button--primary"
              :disabled="!hasSelectedProducts"
              @click="submitProductsHandler"
            >
              {{ $t('common.actions.continue') }}
            </button>
          </div>
        </NavigationBar>
      </div>
      <BottomDrawer
        v-if="isMobileView"
        :is-open="isDrawerOpen"
        @close="toggleDrawer"
      >
        <template #header>
          <h2 class="title-medium">
            {{ $t('searchResult.navigation.drawerTitel') }}
          </h2>
        </template>
        <MyTravel
          :origin="origin"
          :destination="destination"
          :hasReturnTravel="hasReturnTravel"
          :isRebooking="isRebooking"
          :showTotalPrice="false"
        />
        <template #footer>
          <button
            class="button button--medium button--secondary button--fullwidth"
            @click="toggleDrawer"
          >
            {{ $t('common.actions.close') }}
          </button>
        </template>
      </BottomDrawer>
    </section>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    ref,
    computed,
    onMounted,
    onUnmounted,
    watch,
  } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useUserStore } from '@/stores/userStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { useRebookingProducts } from '@/composables/rebooking/useRebookingProducts'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import {
    searchProducts,
    submitAdditionalProducts,
    patchProducts,
    submitProducts,
    fetchBookingData,
  } from '@/services/journeyService'
  import type { Segment, RequiredProduct } from '@/types/bookingTypes'
  import PriceTag from '@/components/TravelPlanner/PriceTag.vue'
  import chevron from '@/assets/icons/chevron.svg'
  import TabContainer from '@/components/BaseComponents/TabContainer/TabContainer.vue'
  import Equipment from '@/components/TravelPlanner/Equipment/Equipment.vue'
  import TravelPlannerHero from '@/components/TravelPlanner/TravelPlannerHero.vue'
  import MyTravel from '@/components/TravelPlanner/MyTravel/MyTravel.vue'
  import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue'
  import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'

  export default defineComponent({
    name: 'EquipmentPage',
    components: {
      TabContainer,
      Equipment,
      TravelPlannerHero,
      MyTravel,
      NavigationBar,
      PriceTag,
      BottomDrawer,
    },
    setup() {
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const myTravelStore = useMyTravelStore()
      const userStore = useUserStore()
      const errorStore = useErrorStore()
      const seatReservationStore = useSeatReservationStore()
      const router = useRouter()
      const route = useRoute()
      const currentBookingNumber = ref<string | null>(null)
      const outboundServiceIdentifier = ref<string | null>(null)
      const inboundServiceIdentifier = ref<string | null>(null)
      const selectedOutboundProductsWithIds = ref<{
        [key: string]: SelectedProduct
      }>({})
      const selectedInboundProductsWithIds = ref<{
        [key: string]: SelectedProduct
      }>({})
      const outboundLegs = ref<any[]>([])
      const inboundLegs = ref<any[]>([])
      const isLoading = ref(true)
      const hasTransfers = ref(false)
      const outboundProducts = ref<ProductSegment[]>([])
      const inboundProducts = ref<ProductSegment[]>([])
      const isDrawerOpen = ref(false)
      const isMobileView = ref(false)
      const selectedOutboundProducts = ref<{ [key: string]: number }>({})
      const selectedInboundProducts = ref<{ [key: string]: number }>({})
      const activeTab = ref(1)

      interface Amount {
        amount: number
        currency_code: string
      }

      interface Price {
        amount: Amount
        currency_code: string
      }

      interface PassengerFare {
        passenger_id: string
        price: Price
        original_price: Price
        inventory_class: string
        tariff_code: string
      }

      interface Product {
        product_code: string
        product_type: string
        passenger_fares: PassengerFare[]
        tariff_code: string
        name: string
        description?: string
        max_quantity?: number
        cancelled?: boolean | string
        uuid?: string
      }

      interface SelectedProduct {
        count: number
        itemIds: string[]
      }

      interface ProductSegment {
        products: Array<{
          product_code: string
          product_type: string
          description?: string
          passenger_fares: Array<{
            price: {
              amount: number
              currency_code: string
            }
            passenger_client_ref: string
            tariff_code: string
            original_price: {
              amount: number
              currency_code: string
            }
            inventory_class: string
          }>
        }>
      }

      const AVAILABLE_PRODUCT_CODES = [
        { code: 'DG' },
        { code: 'BK' },
        { code: 'FB' },
        { code: 'LU' },
        { code: 'PR' },
        { code: 'VC' },
        { code: 'IN' },
        { code: 'ET' },
        { code: 'DS' },
        { code: 'PE' },
        { code: 'HO' },
        { code: 'WI' },
        { code: 'MD' },
        { code: 'ME' },
        { code: 'TA' },
        { code: 'PA' },
        { code: 'LT' },
        { code: 'AU' },
      ]

      const fetchAvailableProducts = async (
        legs: any[],
        direction: string
      ): Promise<ProductSegment[]> => {
        
        if (!legs.length) {
          return []
        }

        const uniqueLegs = legs.filter(
          (leg, index, self) =>
            index ===
            self.findIndex(
              (s) => s.service_identifier === leg.service_identifier
            )
        )

        const productsPerLeg = await Promise.all(
          uniqueLegs.map(async (leg, index) => {            
            try {
              if (!leg.service_identifier) {
                return { products: [] }
              }
              try {
                const response = await searchProducts(
                  leg.service_identifier,
                  direction,
                  leg.departure_station._u_i_c_station_code,
                  leg.arrival_station._u_i_c_station_code,
                  leg.service_schedule_date || leg.travel_date,
                  AVAILABLE_PRODUCT_CODES
                )

                const products = (response.data.segment.products || [])
                  .filter((product: any) => !product.cancelled)
                  .map((product: any) => ({
                    ...product,
                    name: product.name || product.product_code || '',
                    passenger_fares: product.passenger_fares.map(
                      (fare: any) => ({
                        passenger_id: fare.passenger_client_ref,
                        price: fare.price
                          ? {
                              amount:
                                typeof fare.price.amount === 'number'
                                  ? fare.price.amount / 100
                                  : 0,
                              currency_code: fare.price.currency_code || 'NOK',
                            }
                          : null,
                        original_price: fare.original_price
                          ? {
                              amount: fare.original_price.amount / 100,
                              currency_code:
                                fare.original_price.currency_code || 'NOK',
                            }
                          : null,
                        inventory_class: fare.inventory_class || 'STANDARD',
                        tariff_code: fare.tariff_code,
                      })
                    ),
                  }))

                return {
                  products,
                }
              } catch (error) {
                console.error('Error fetching products for leg:', error)
                return { products: [] }
              }
            } catch (error) {
              console.error('Error fetching products for leg:', error)
              return { products: [] }
            }
          })
        )

        if (productsPerLeg.length === 0) {
          return []
        }

        const firstLegProducts = productsPerLeg[0]?.products || []
        let commonProducts = firstLegProducts

        for (let i = 1; i < productsPerLeg.length; i++) {
          const legProducts = productsPerLeg[i]?.products || []
          commonProducts = commonProducts.filter(
            (product: { product_code: string }) => {
              return legProducts.some(
                (p: { product_code: string }) =>
                  p.product_code === product.product_code
              )
            }
          )
        }

        return [{ products: commonProducts }]
      }

      const formatSegment = (
        direction: 'outbound' | 'inbound',
        products: any[]
      ) => {
        const segments =
          direction === 'outbound'
            ? bookingStore.bookingData?.outbound_booking_tariff_segments || []
            : bookingStore.bookingData?.inbound_booking_tariff_segments || []

        if (!segments.length) {
          console.warn(
            `[EquipmentPage] No ${direction} segments found in booking data`
          )
          return null
        }

        const segment = segments[0]

        return {
          id: segment.id,
          origin: segment.departure_station._u_i_c_station_code,
          destination: segment.arrival_station._u_i_c_station_code,
          direction,
          service_name:
            segment.booking_journey_segments?.[0]?.service_name || '',
          service_identifier: segment.service_identifier,
          start_validity_date: segment.validity_start_date,
          service_type:
            segment.booking_journey_segments?.[0]?.service_type?.code || '',
          items: products
            .filter((product) => product.passenger_fares?.[0]?.passenger_id)
            .map((product) => ({
              passenger_id: product.passenger_fares[0].passenger_id,
              tariff_code:
                product.tariff_code || product.passenger_fares[0].tariff_code,
            })),
        }
      }

      const initializeBookingData = async () => {
        let retryCount = 0
        let data

        const bookingNum =
          journeyStore.bookingData?.booking_number || bookingStore.bookingNumber

        if (!bookingNum) {
          console.error('[EquipmentPage] No booking number available')
          router.push('/reiseplanlegger/velg-reise')
          return
        }

        currentBookingNumber.value = bookingNum

        if (!bookingStore.bookingData?.booking_number) {
          try {
            const response = await fetchBookingData(bookingNum)
            if (response?.data?.booking) {
              bookingStore.setBookingData(response.data.booking)
            }
          } catch (error) {
            console.error('[EquipmentPage] Error fetching booking data:', error)
            isLoading.value = false
            return
          }
        }

        while (retryCount < 5) {
          data = bookingStore.bookingData
          if (data?.booking_number) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
          retryCount++
        }

        if (!data?.booking_number) {
          isLoading.value = false
          return
        }

        await initializeFromStoreData()

        if (myTravelStore.isRebooking) {
          const { loadOriginalSeats, loadOriginalEquipment } =
            useRebookingProducts()
          loadOriginalSeats('outbound')
          loadOriginalSeats('inbound')

          loadOriginalEquipment('outbound')
          loadOriginalEquipment('inbound')

          syncEquipmentWithMyTravelStore('outbound')
          syncEquipmentWithMyTravelStore('inbound')

        }

        try {

          const outboundSegments = bookingStore.bookingData?.outbound_booking_tariff_segments || []
          const inboundSegments = bookingStore.bookingData?.inbound_booking_tariff_segments || []
          
          outboundSegments.forEach((segment, index) => {
            console.log(`[DEBUG] EquipmentPage outbound segment ${index} required_products:`, {
              service_identifier: segment.service_identifier,
              has_required_products: !!(segment.required_products && segment.required_products.length > 0),
              required_products_count: segment.required_products?.length || 0,
              required_products_summary: segment.required_products?.map(p => ({ 
                id: p.id, 
                name: p.name, 
                cancelled: p.cancelled,
                product_type: p.product_type 
              })) || []
            })
          })
          
          const journeyOutboundSegments = journeyStore.bookingData?.outbound_booking_tariff_segments || []
        

          if (
            bookingStore.bookingData?.outbound_booking_tariff_segments &&
            bookingStore.bookingData.outbound_booking_tariff_segments.length > 0
          ) {
            const allSegments = bookingStore.bookingData.outbound_booking_tariff_segments

            const filteredByRequiredProducts = allSegments.filter((segment) =>
              segment.required_products?.some(
                (product) => !product.cancelled
              )
            )
                        
            outboundLegs.value = filteredByRequiredProducts
                .filter(
                  (segment, index, self) =>
                    index ===
                    self.findIndex(
                      (s) => s.service_identifier === segment.service_identifier
                    )
                )
                .map((segment) => {
                  const journeySegment = segment.booking_journey_segments?.[0]
                  
                  return {
                    ...segment,
                    departure_station: journeySegment?.departure_station || segment.departure_station,
                    arrival_station: journeySegment?.arrival_station || segment.arrival_station,
                    travel_date: journeySegment?.travel_date || segment.travel_date,
                    service_schedule_date: journeySegment?.service_schedule_date || segment.service_schedule_date,
                    departure_time: journeySegment?.departure_time || segment.departure_time,
                    additional_products:
                      segment.additional_products?.filter(
                        (product) => !product.cancelled
                      ) || [],
                  }
                })

            hasTransfers.value = outboundLegs.value.length > 1

            if (hasTransfers.value) {
              outboundProducts.value = await fetchAvailableProducts(
                outboundLegs.value,
                'outbound'
              )
            } else if (outboundLegs.value.length === 1) {
              const leg = outboundLegs.value[0]
              const serviceDate = leg.service_schedule_date || leg.travel_date

              if (
                !leg.service_identifier ||
                !leg.departure_station?._u_i_c_station_code ||
                !leg.arrival_station?._u_i_c_station_code ||
                !serviceDate
              ) {
                outboundProducts.value = []
              } else {
                try {
                  const response = await searchProducts(
                    leg.service_identifier,
                    'outbound',
                    leg.departure_station._u_i_c_station_code,
                    leg.arrival_station._u_i_c_station_code,
                    serviceDate,
                    AVAILABLE_PRODUCT_CODES
                  )
                outboundProducts.value = [
                  {
                    products: (response.data.segment.products || [])
                      .filter((product: any) => !product.cancelled)
                      .map((product: any) => ({
                        ...product,
                        name: product.name || product.product_code || '',
                        passenger_fares: product.passenger_fares.map(
                          (fare: any) => ({
                            passenger_id: fare.passenger_client_ref,
                            price: fare.price
                              ? {
                                  amount: fare.price.amount / 100, // Standardize price format
                                  currency_code:
                                    fare.price.currency_code || 'NOK',
                                }
                              : null,
                            original_price: fare.original_price
                              ? {
                                  amount: fare.original_price.amount / 100,
                                  currency_code:
                                    fare.original_price.currency_code || 'NOK',
                                }
                              : null,
                            inventory_class: fare.inventory_class || 'STANDARD',
                            tariff_code: fare.tariff_code,
                          })
                        ),
                      })),
                  },
                ]
                } catch (error) {
                  console.error('[DEBUG] EquipmentPage API error:', error)
                  outboundProducts.value = []
                }
              }
            }
          }

          if (
            Array.isArray(
              bookingStore.bookingData?.inbound_booking_tariff_segments
            ) &&
            (bookingStore.bookingData?.inbound_booking_tariff_segments ?? [])
              .length > 0
          ) {
            inboundLegs.value =
              bookingStore.bookingData?.inbound_booking_tariff_segments
                .filter((segment) =>
                  segment.required_products?.some(
                    (product) => !product.cancelled
                  )
                )
                .filter(
                  (segment, index, self) =>
                    index ===
                    self.findIndex(
                      (s) => s.service_identifier === segment.service_identifier
                    )
                )
                .map((segment) => {
                  const journeySegment = segment.booking_journey_segments?.[0]
                  
                  return {
                    ...segment,
                    departure_station: journeySegment?.departure_station || segment.departure_station,
                    arrival_station: journeySegment?.arrival_station || segment.arrival_station,
                    travel_date: journeySegment?.travel_date || segment.travel_date,
                    service_schedule_date: journeySegment?.service_schedule_date || segment.service_schedule_date,
                    departure_time: journeySegment?.departure_time || segment.departure_time,
                    additional_products:
                      segment.additional_products?.filter(
                        (product) => !product.cancelled
                      ) || [],
                  }
                })

            const hasInboundTransfers = inboundLegs.value.length > 1
            if (hasInboundTransfers) {
              inboundProducts.value = await fetchAvailableProducts(
                inboundLegs.value,
                'inbound'
              )
            } else if (inboundLegs.value.length === 1) {
              const leg = inboundLegs.value[0]
              const response = await searchProducts(
                leg.service_identifier,
                'inbound',
                leg.departure_station._u_i_c_station_code,
                leg.arrival_station._u_i_c_station_code,
                leg.service_schedule_date,
                AVAILABLE_PRODUCT_CODES
              )
              inboundProducts.value = [
                {
                  products: (response.data.segment.products || [])
                    .filter((product: any) => !product.cancelled)
                    .map((product: any) => ({
                      ...product,
                      name: product.name || product.product_code || '',
                      passenger_fares: product.passenger_fares.map(
                        (fare: any) => ({
                          passenger_id: fare.passenger_client_ref,
                          price: fare.price
                            ? {
                                amount: fare.price.amount / 100,
                                currency_code:
                                  fare.price.currency_code || 'NOK',
                              }
                            : null,
                          original_price: fare.original_price
                            ? {
                                amount: fare.original_price.amount / 100,
                                currency_code:
                                  fare.original_price.currency_code || 'NOK',
                              }
                            : null,
                          inventory_class: fare.inventory_class || 'STANDARD',
                          tariff_code: fare.tariff_code,
                        })
                      ),
                    })),
                },
              ]
            }
          } 
        } catch (error) {
          console.error(
            '[EquipmentPage] Error fetching available products:',
            error
          );
          outboundProducts.value = []
          inboundProducts.value = []
        }

        const hasOutboundProducts =
          Array.isArray(outboundProducts.value) &&
          outboundProducts.value.some(
            (segment) =>
              segment?.products &&
              Array.isArray(segment.products) &&
              segment.products.length > 0
          )

        const hasInboundProducts =
          Array.isArray(inboundProducts.value) &&
          inboundProducts.value.some(
            (segment) =>
              segment?.products &&
              Array.isArray(segment.products) &&
              segment.products.length > 0
          )

        if (!hasOutboundProducts && !hasInboundProducts) {
          console.log('[DEBUG-REDIRECT] No equipment available, preparing redirect with route query:', {
            routeQuery: route.query,
            currentUrl: window.location.href,
            hasOutboundProducts,
            hasInboundProducts
          })
          
          const queryParams: any = {
            bookingId: route.query.bookingId || currentBookingNumber.value,
            noEquipment: 'true'
          }

          // Preserve all rebooking parameters if they exist
          if (route.query.isRebooking) {
            queryParams.isRebooking = route.query.isRebooking
          }
          if (route.query.rebookingType) {
            queryParams.rebookingType = route.query.rebookingType
          }
          if (route.query.itemsToReplace) {
            queryParams.itemsToReplace = route.query.itemsToReplace
          }

          console.log('[DEBUG-REDIRECT] Final query params for redirect:', queryParams)

          await router.push({
            name: userStore.isLoggedIn
              ? 'payment'
              : 'PersonalInformation',
            query: queryParams
          })
          return
        }
        isLoading.value = false
      }

      const initializeFromStoreData = async () => {
        selectedOutboundProducts.value = {}
        selectedInboundProducts.value = {}
        selectedOutboundProductsWithIds.value = {}
        selectedInboundProductsWithIds.value = {}

        const myTravelOutboundEquipment = myTravelStore.outboundEquipment || []
        const bookingStoreOutboundEquipment =
          bookingStore.bookingEquipment?.outbound || []
        const outboundEquipment = [
          ...myTravelOutboundEquipment,
          ...bookingStoreOutboundEquipment,
        ]
        const myTravelInboundEquipment = myTravelStore.inboundEquipment || []
        const bookingStoreInboundEquipment =
          bookingStore.bookingEquipment?.inbound || []
        const inboundEquipment = [
          ...myTravelInboundEquipment,
          ...bookingStoreInboundEquipment,
        ]

        type ProductLike = {
          code: string
          cancelled?: boolean | string | number
          [key: string]: any
        }

        const getProductCode = (product: ProductLike): string => {
          return (product as any).product_code || product.code
        }

        const isCancelled = (product: ProductLike): boolean => {
          if (product.cancelled === undefined || product.cancelled === null) {
            return false
          }
          const cancelledValue = String(product.cancelled).toLowerCase()
          return cancelledValue === 'true' || cancelledValue === '1'
        }

        if (isRebooking.value) {
          if (bookingStore.bookingData) {
            const outboundSegments =
              bookingStore.bookingData.outbound_booking_tariff_segments || []

            outboundSegments.forEach((segment, index) => {
              const activeEquipment = (
                segment.additional_products || []
              ).filter((product) => {
                return !isCancelled(product)
              })

              activeEquipment.forEach((product) => {
                const productCode = getProductCode(product)
                const passengerId = product.passenger_fares?.[0]?.passenger_id

                if (productCode) {
                  if (!selectedOutboundProducts.value[productCode]) {
                    selectedOutboundProducts.value[productCode] = 0
                  }
                  selectedOutboundProducts.value[productCode] += 1

                  if (passengerId) {
                    if (!selectedOutboundProductsWithIds.value[productCode]) {
                      selectedOutboundProductsWithIds.value[productCode] = {
                        count: 0,
                        itemIds: [],
                      }
                    }
                    selectedOutboundProductsWithIds.value[
                      productCode
                    ].count += 1
                    selectedOutboundProductsWithIds.value[
                      productCode
                    ].itemIds.push(passengerId)
                  }
                }
              })
            })

            if (Object.keys(selectedOutboundProducts.value).length > 0) {
              const outboundEquipmentArray = Object.entries(
                selectedOutboundProducts.value
              ).map(([code, count]) => ({
                product_code: code,
                count,
                cancelled: false,
              }))

              if (outboundEquipmentArray.length > 0) {
                bookingStore.bookingEquipment.outbound = outboundEquipmentArray
              }
            }

            const inboundSegments =
              bookingStore.bookingData.inbound_booking_tariff_segments || []

            if (inboundSegments.length > 0) {
              inboundSegments.forEach((segment, index) => {
                const activeEquipment = (
                  segment.additional_products || []
                ).filter((product) => {
                  return !isCancelled(product)
                })

                activeEquipment.forEach((product) => {
                  const productCode = getProductCode(product)
                  const passengerId = product.passenger_fares?.[0]?.passenger_id

                  if (productCode) {
                    if (!selectedInboundProducts.value[productCode]) {
                      selectedInboundProducts.value[productCode] = 0
                    }
                    selectedInboundProducts.value[productCode] += 1

                    if (passengerId) {
                      if (!selectedInboundProductsWithIds.value[productCode]) {
                        selectedInboundProductsWithIds.value[productCode] = {
                          count: 0,
                          itemIds: [],
                        }
                      }
                      selectedInboundProductsWithIds.value[
                        productCode
                      ].count += 1
                      selectedInboundProductsWithIds.value[
                        productCode
                      ].itemIds.push(passengerId)
                    }
                  }
                })
              })

              if (Object.keys(selectedInboundProducts.value).length > 0) {
                const inboundEquipmentArray = Object.entries(
                  selectedInboundProducts.value
                ).map(([code, count]) => ({
                  product_code: code,
                  count,
                  cancelled: false,
                }))

                if (inboundEquipmentArray.length > 0) {
                  bookingStore.bookingEquipment.inbound = inboundEquipmentArray
                }
              }
            }
          }
        } else {
          outboundEquipment
            .filter((product) => {
              const isProductCancelled = isCancelled(product)
              return !isProductCancelled
            })
            .forEach((product) => {
              const productCode = product.product_code || product.code
              const passengerId = product.passenger_fares?.[0]?.passenger_id

              if (productCode && passengerId) {
                if (!selectedOutboundProducts.value[productCode]) {
                  selectedOutboundProducts.value[productCode] = 0
                }
                if (!selectedOutboundProductsWithIds.value[productCode]) {
                  selectedOutboundProductsWithIds.value[productCode] = {
                    count: 0,
                    itemIds: [],
                  }
                }

                selectedOutboundProducts.value[productCode]++
                const productWithIds =
                  selectedOutboundProductsWithIds.value[productCode]
                productWithIds.count++
                productWithIds.itemIds.push(passengerId)
              }
            })

          inboundEquipment
            .filter((product) => {
              const isProductCancelled = isCancelled(product)
              return !isProductCancelled
            })
            .forEach((product) => {
              const productCode = product.product_code || product.code
              const passengerId = product.passenger_fares?.[0]?.passenger_id

              if (productCode && passengerId) {
                if (!selectedInboundProducts.value[productCode]) {
                  selectedInboundProducts.value[productCode] = 0
                }
                if (!selectedInboundProductsWithIds.value[productCode]) {
                  selectedInboundProductsWithIds.value[productCode] = {
                    count: 0,
                    itemIds: [],
                  }
                }

                selectedInboundProducts.value[productCode]++
                const productWithIds =
                  selectedInboundProductsWithIds.value[productCode]
                productWithIds.count++
                productWithIds.itemIds.push(passengerId)
              }
            })
        }
        syncEquipmentWithMyTravelStore('outbound')
        syncEquipmentWithMyTravelStore('inbound')
      }

      const syncEquipmentWithMyTravelStore = (
        direction: 'outbound' | 'inbound'
      ) => {
        const selectedProducts =
          direction === 'outbound'
            ? selectedOutboundProducts.value
            : selectedInboundProducts.value

        const sourceProducts =
          direction === 'outbound'
            ? outboundProducts.value.flatMap((p) => p.products)
            : inboundProducts.value.flatMap((p) => p.products)

        const existingEquipment =
          direction === 'outbound'
            ? myTravelStore.outboundEquipment
            : myTravelStore.inboundEquipment

        const products = Object.entries(selectedProducts).map(
          ([code, count]) => {
            const sourceProduct = sourceProducts.find(
              (p) => p.product_code === code || (p as any).code === code
            ) as Product | undefined

            return {
              product_code: code,
              count,
              ...sourceProduct,
            }
          }
        )

        if (existingEquipment && existingEquipment.length > 0) {
          existingEquipment.forEach((equipment) => {
            if (
              equipment.product_code &&
              !selectedProducts[equipment.product_code]
            ) {
              let cancelled: boolean | string | undefined = undefined
              if (equipment.cancelled !== undefined) {
                if (typeof equipment.cancelled === 'number') {
                  cancelled = equipment.cancelled !== 0
                } else {
                  cancelled = equipment.cancelled
                }
              }

              const normalizedEquipment = {
                ...equipment,
                count: equipment.count || 1,
                cancelled,
                passenger_fares:
                  equipment.passenger_fares?.map((fare) => {
                    const defaultPrice = {
                      amount: 0,
                      currency_code: 'NOK',
                    }

                    return {
                      passenger_id: fare.passenger_id || '',
                      price: fare.price || defaultPrice,
                      original_price: fare.original_price || defaultPrice,
                      inventory_class: fare.inventory_class || 'STANDARD',
                      tariff_code: fare.tariff_code || '',
                    }
                  }) || [],
              }

              if (normalizedEquipment.passenger_fares.length === 0) {
                normalizedEquipment.passenger_fares = [
                  {
                    passenger_id: '',
                    price: {
                      amount: 0,
                      currency_code: 'NOK',
                    },
                    original_price: {
                      amount: 0,
                      currency_code: 'NOK',
                    },
                    inventory_class: 'STANDARD',
                    tariff_code: '',
                  },
                ]
              }

              products.push(normalizedEquipment)
            }
          })
        }

        let filteredProducts = products.filter((p) => p.count > 0)

        filteredProducts = filteredProducts.filter((p) => {
          const productType = (p.product_type || '').toUpperCase()
          const type = (p.type || '').toUpperCase()
          const productCode = (p.product_code || '').toUpperCase()
          const name = (p.name || '').toUpperCase()

          if (
            productType === 'SF' ||
            productType === 'SEAT' ||
            type === 'SF' ||
            type === 'SEAT' ||
            productCode === 'COMFORT_SEAT' ||
            productCode === 'FRONT_SEAT'
          ) {
            const hasSeterPrice = (() => {
              // Handle price as an object with amount property
              if (
                typeof (p as any).price === 'object' &&
                (p as any).price !== null
              ) {
                return (p as any).price.amount === 59
              }
              // Handle price as a direct number
              if (typeof (p as any).price === 'number') {
                return (p as any).price === 59
              }
              // Handle price in passenger_fares
              if (p.passenger_fares && p.passenger_fares.length > 0) {
                const passengerPrice = p.passenger_fares[0].price
                if (!passengerPrice) return false

                if (
                  typeof passengerPrice.amount === 'object' &&
                  passengerPrice.amount !== null
                ) {
                  return passengerPrice.amount.amount === 59
                }

                if (typeof passengerPrice.amount === 'number') {
                  return passengerPrice.amount === 59
                }
              }
              return false
            })()

            if (productCode === 'SETER' && hasSeterPrice) {
              console.log(
                `[DEBUG] EquipmentPage: Keeping special 'seter' product with price 59`
              )
              return true
            }

            console.log(
              `[DEBUG] EquipmentPage: Filtering out seat product: ${
                p.name || p.code || p.product_code
              }`
            )
            return false
          }

          return true
        })

        // Filter out cancelled products
        const cancelledProducts = filteredProducts.filter((p) => {
          // Check if the product is a bicycle with zero price (likely cancelled)
          // Handle different possible price structures
          const hasZeroPrice = (() => {
            // Handle price as an object with amount property
            if (
              typeof (p as any).price === 'object' &&
              (p as any).price !== null
            ) {
              return !(p as any).price.amount || (p as any).price.amount === 0
            }
            // Handle price as a direct number
            if (typeof (p as any).price === 'number') {
              return (p as any).price === 0
            }
            // Handle price in passenger_fares
            if (p.passenger_fares && p.passenger_fares.length > 0) {
              // Check if price exists and has an amount property
              const passengerPrice = p.passenger_fares[0].price
              if (!passengerPrice) return true // No price means zero price

              // Handle amount as an object with amount property
              if (
                typeof passengerPrice.amount === 'object' &&
                passengerPrice.amount !== null
              ) {
                return (
                  !passengerPrice.amount.amount ||
                  passengerPrice.amount.amount === 0
                )
              }

              // Handle amount as a direct number
              if (typeof passengerPrice.amount === 'number') {
                return passengerPrice.amount === 0
              }

              return true // Default to true if amount is in an unexpected format
            }
            // Default to true if no price found
            return true
          })()

          if (
            (p.product_code === 'BICYCLE' ||
              p.product_code === 'SYKKEL' ||
              (p.name && p.name.toLowerCase().includes('sykkel'))) &&
            hasZeroPrice
          ) {
            return true
          }

          if (p.count === 0) {
            return true
          }

          if (p.cancelled !== undefined) {
            if (typeof p.cancelled === 'boolean' && p.cancelled === true)
              return true
            if (
              typeof p.cancelled === 'string' &&
              (p.cancelled === 'true' || p.cancelled === '1')
            )
              return true
            if (typeof p.cancelled === 'number' && p.cancelled === 1)
              return true
          }

          if (p.uuid && bookingStore.bookingData) {
            const bookingProducts =
              bookingStore.bookingData?.[
                direction === 'outbound'
                  ? 'outbound_booking_tariff_segments'
                  : 'inbound_booking_tariff_segments'
              ] || []

            for (const segment of bookingProducts) {
              const additionalProducts = segment.additional_products || []
              const isProductCancelled = additionalProducts.some((ap) => {
                const productsMatch =
                  (ap.uuid && p.uuid && ap.uuid === p.uuid) ||
                  (ap.code && p.product_code && ap.code === p.product_code) ||
                  (ap.code && (p as any).code && ap.code === (p as any).code)

                if (!productsMatch) return false

                const cancellationStatus = ap.cancelled

                if (typeof cancellationStatus === 'boolean')
                  return cancellationStatus === true
                if (typeof cancellationStatus === 'string')
                  return (
                    cancellationStatus === 'true' || cancellationStatus === '1'
                  )
                if (typeof cancellationStatus === 'number')
                  return cancellationStatus === 1

                return false
              })

              if (isProductCancelled) {
                return true
              }
            }
          }

          return false
        })

        // Remove cancelled products from the filtered list
        if (cancelledProducts.length > 0) {
          const cleanFilteredProducts = filteredProducts.filter((p) => {
            if (p.cancelled === undefined) {
              if (p.uuid) {
                const bookingProducts =
                  bookingStore.bookingData?.[
                    direction === 'outbound'
                      ? 'outbound_booking_tariff_segments'
                      : 'inbound_booking_tariff_segments'
                  ] || []

                for (const segment of bookingProducts) {
                  const additionalProducts = segment.additional_products || []
                  const isProductCancelled = additionalProducts.some((ap) => {
                    const productsMatch =
                      (ap.uuid && p.uuid && ap.uuid === p.uuid) ||
                      (ap.code &&
                        p.product_code &&
                        ap.code === p.product_code) ||
                      (ap.code &&
                        (p as any).code &&
                        ap.code === (p as any).code)

                    if (!productsMatch) return false

                    const cancellationStatus = ap.cancelled

                    if (typeof cancellationStatus === 'boolean')
                      return cancellationStatus === true
                    if (typeof cancellationStatus === 'string')
                      return (
                        cancellationStatus === 'true' ||
                        cancellationStatus === '1'
                      )
                    if (typeof cancellationStatus === 'number')
                      return cancellationStatus === 1

                    return false
                  })
                  if (isProductCancelled) return false
                }
              }
              return true
            }

            if (typeof p.cancelled === 'boolean') return p.cancelled !== true
            if (typeof p.cancelled === 'string')
              return p.cancelled !== 'true' && p.cancelled !== '1'
            if (typeof p.cancelled === 'number') return p.cancelled !== 1
            return true
          })
          filteredProducts = cleanFilteredProducts
        }

        // Ensure prices are correctly normalized before updating the store
        filteredProducts = filteredProducts.map((product) => {
          const normalizedProduct = { ...product }

          // Special case handling for known products with fixed prices
          if (
            normalizedProduct.product_code === 'SPECIAL_LUGGAGE' ||
            (normalizedProduct.name &&
              normalizedProduct.name.includes('Spesialbagasje'))
          ) {
            // No hardcoded prices allowed - must come from API
            console.warn('[EQUIPMENT] Product missing price data:', {
              productCode: normalizedProduct.product_code,
              name: normalizedProduct.name,
              hasPrice: !!normalizedProduct.price
            })
          } else {
            // Normalize price if it exists and is in øre (greater than 1000)
            if (
              normalizedProduct.price?.amount &&
              normalizedProduct.price.amount > 1000
            ) {
              normalizedProduct.price = {
                ...normalizedProduct.price,
                amount: normalizedProduct.price.amount / 100,
              }
            }

            // Normalize passenger fares prices if they exist
            if (
              normalizedProduct.passenger_fares &&
              Array.isArray(normalizedProduct.passenger_fares)
            ) {
              normalizedProduct.passenger_fares =
                normalizedProduct.passenger_fares.map((fare) => {
                  if (fare.price?.amount && fare.price.amount > 1000) {
                    return {
                      ...fare,
                      price: {
                        ...fare.price,
                        amount: fare.price.amount / 100,
                      },
                    }
                  }
                  return fare
                })
            }
          }

          // Ensure we don't have zero prices for non-cancelled items
          if (
            !normalizedProduct.cancelled &&
            (!normalizedProduct.price || normalizedProduct.price.amount === 0)
          ) {
            // Try to get price from passenger_fares if direct price is zero
            if (
              Array.isArray(normalizedProduct.passenger_fares) &&
              normalizedProduct.passenger_fares.length > 0
            ) {
              const passengerFare = normalizedProduct.passenger_fares[0]
              if (passengerFare?.price?.amount) {
                normalizedProduct.price = {
                  amount: passengerFare.price.amount,
                  currency: passengerFare.price.currency || 'NOK',
                }
              }
            }
          }

          return normalizedProduct
        })

        // Update the myTravelStore with the filtered products
        if (filteredProducts.length > 0) {
          myTravelStore.updateGroupedEquipment(
            direction,
            filteredProducts,
            true
          )
          myTravelStore.recalculateAllPrices()
        }
      }

      const addProduct = (product: any, direction: 'outbound' | 'inbound') => {
        const productCode = product.product_code || product.code
        const passengerId = product.passenger_fares?.[0]?.passenger_id
        const productType = product.product_type?.toUpperCase()

        if (!productCode || !passengerId) {
          console.error('[EquipmentPage] Invalid product data:', {
            product,
            direction,
          })
          return
        }

        if (direction === 'outbound') {
          if (!selectedOutboundProducts.value[productCode]) {
            selectedOutboundProducts.value[productCode] = 0
          }
          if (!selectedOutboundProductsWithIds.value[productCode]) {
            selectedOutboundProductsWithIds.value[productCode] = {
              count: 0,
              itemIds: [],
            }
          }
        } else {
          if (!selectedInboundProducts.value[productCode]) {
            selectedInboundProducts.value[productCode] = 0
          }
          if (!selectedInboundProductsWithIds.value[productCode]) {
            selectedInboundProductsWithIds.value[productCode] = {
              count: 0,
              itemIds: [],
            }
          }
        }

        const newProduct = {
          ...product,
          passenger_id: passengerId,
          passenger_fares: [
            {
              ...product.passenger_fares[0],
              passenger_id: passengerId,
            },
          ],
        }

        if (direction === 'outbound') {
          selectedOutboundProducts.value[productCode]++
          const productWithIds =
            selectedOutboundProductsWithIds.value[productCode]
          productWithIds.count++
          productWithIds.itemIds.push(passengerId)
          bookingStore.addOutboundEquipment(newProduct)
          syncEquipmentWithMyTravelStore('outbound')
        } else {
          selectedInboundProducts.value[productCode]++
          const productWithIds =
            selectedInboundProductsWithIds.value[productCode]
          productWithIds.count++
          productWithIds.itemIds.push(passengerId)
          bookingStore.addInboundEquipment(newProduct)
          syncEquipmentWithMyTravelStore('inbound')
        }
      }

      const removeProduct = (
        product: any,
        direction: 'outbound' | 'inbound'
      ) => {
        const prevCount =
          direction === 'outbound'
            ? selectedOutboundProducts.value[product.product_code] || 0
            : selectedInboundProducts.value[product.product_code] || 0
        const productCode = product.product_code || product.code
        const passengerId = product.passenger_fares?.[0]?.passenger_id

        if (!productCode || !passengerId) {
          return
        }

        if (
          direction === 'outbound' &&
          selectedOutboundProducts.value[productCode] > 0
        ) {
          selectedOutboundProducts.value[productCode]--
          const productWithIds =
            selectedOutboundProductsWithIds.value[productCode]
          if (productWithIds) {
            productWithIds.count = Math.max(0, productWithIds.count - 1)
            const idIndex = productWithIds.itemIds.indexOf(passengerId)
            if (idIndex !== -1) {
              productWithIds.itemIds.splice(idIndex, 1)
            }
          }
          syncEquipmentWithMyTravelStore('outbound')
        } else if (
          direction === 'inbound' &&
          selectedInboundProducts.value[productCode] > 0
        ) {
          selectedInboundProducts.value[productCode]--
          const productWithIds =
            selectedInboundProductsWithIds.value[productCode]
          if (productWithIds) {
            productWithIds.count = Math.max(0, productWithIds.count - 1)
            const idIndex = productWithIds.itemIds.indexOf(passengerId)
            if (idIndex !== -1) {
              productWithIds.itemIds.splice(idIndex, 1)
            }
          }
          syncEquipmentWithMyTravelStore('inbound')
        }
      }

      const submitProductsHandler = async () => {
        try {
          const selectedEquipment = bookingStore.selectedEquipment
          if (!currentBookingNumber.value) {
            throw new Error('Booking number is missing')
          }

          const segments: Array<{
            origin: string
            origin_name: string
            destination: string
            destination_name: string
            start_validity_date: string
            start_validity_time: string
            direction: string
            service_name: string
            service_identifier: string
            items: Array<{
              tariff_code: string
              passenger_id: string
            }>
          }> = []

          if (selectedEquipment.outbound.length > 0) {
            outboundLegs.value.forEach((leg) => {
              const serviceId = leg.service_identifier
              if (serviceId) {
                const validityDate =
                  bookingStore.bookingData
                    ?.outbound_booking_tariff_segments?.[0]?.validity_start_date
                if (!validityDate) {
                  throw new Error('Missing validity date for outbound segment')
                }

                segments.push({
                  origin: leg.departure_station._u_i_c_station_code,
                  origin_name: leg.departure_station.name,
                  destination: leg.arrival_station._u_i_c_station_code,
                  destination_name: leg.arrival_station.name,
                  start_validity_date: validityDate,
                  start_validity_time: leg.departure_time || '00:00',
                  direction: 'outbound',
                  service_name: leg.service_name || '',
                  service_identifier: serviceId,
                  items: selectedEquipment.outbound.map((product) => ({
                    tariff_code: product.passenger_fares[0].tariff_code || '',
                    passenger_id: product.passenger_fares[0].passenger_id,
                  })),
                })
              }
            })
          }

          if (selectedEquipment.inbound.length > 0) {
            inboundLegs.value.forEach((leg) => {
              const serviceId = leg.service_identifier
              if (serviceId) {
                const validityDate =
                  bookingStore.bookingData?.inbound_booking_tariff_segments?.[0]
                    ?.validity_start_date
                if (!validityDate) {
                  throw new Error('Missing validity date for inbound segment')
                }

                segments.push({
                  origin: leg.departure_station._u_i_c_station_code,
                  origin_name: leg.departure_station.name,
                  destination: leg.arrival_station._u_i_c_station_code,
                  destination_name: leg.arrival_station.name,
                  start_validity_date: validityDate,
                  start_validity_time: leg.departure_time || '00:00',
                  direction: 'inbound',
                  service_name: leg.service_name || '',
                  service_identifier: serviceId,
                  items: selectedEquipment.inbound.map((product) => ({
                    tariff_code: product.passenger_fares[0].tariff_code || '',
                    passenger_id: product.passenger_fares[0].passenger_id,
                  })),
                })
              }
            })
          }

          const uniquePassengerIds = new Set([
            ...selectedEquipment.outbound.map(
              (p) => p.passenger_fares[0].passenger_id
            ),
            ...selectedEquipment.inbound.map(
              (p) => p.passenger_fares[0].passenger_id
            ),
          ])

          const passengers = Array.from(uniquePassengerIds).map((id) => ({
            id,
            type: 'ADT',
          }))

          if (segments.length > 0) {
            const submitResponse = await submitProducts(
              currentBookingNumber.value,
              segments,
              passengers,
              'NOK'
            )

            if (submitResponse?.data?.booking_number) {
              bookingStore.setBookingNumber(submitResponse.data.booking_number)
            }

            const response = await fetchBookingData(currentBookingNumber.value)
            if (response?.data?.booking) {
              await bookingStore.setBookingData(response.data.booking)
            }
          }

          await router.push({
            name: route.query.isRebooking
              ? 'payment'
              : userStore.isLoggedIn
              ? 'payment'
              : 'PersonalInformation',
            query: {
              isRebooking: route.query.isRebooking,
              rebookingType: route.query.rebookingType,
              bookingId: route.query.bookingId || currentBookingNumber.value,
              itemsToReplace: route.query.itemsToReplace,
            },
          })
        } catch (error) {
          console.error('Error submitting products:', error)
          errorStore.setError('Det oppstod en feil under lagring av utstyr')
        }
      }

      const checkMobileView = () => {
        isMobileView.value = window.innerWidth < 674
      }

      onMounted(async () => {
        await initializeBookingData()
        checkMobileView()
        window.addEventListener('resize', checkMobileView)
      })

      onUnmounted(() => {
        window.removeEventListener('resize', checkMobileView)
      })

      const handleBack = () => {
        if (route.query.isRebooking) {
          const queryParams = {
            isRebooking: route.query.isRebooking,
            rebookingType: route.query.rebookingType,
            bookingId: route.query.bookingId,
            itemsToReplace: route.query.itemsToReplace
          }

          router.push({
            name: 'RebookingSearchResultPage',
            query: queryParams,
          })
        } else {
          router.push('/reiseplanlegger/velg-reise')
        }
      }

      const isRebooking = computed(() => myTravelStore.isInRebookingMode)
      const origin = computed(() => journeyStore.OriginStation.name)
      const destination = computed(() => journeyStore.destinationStation.name)
      const hasReturnTravel = computed(() => !!journeyStore.returnDate)
      const hasSelectedProducts = computed(() => true)
      const hasInboundProducts = computed(
        () => inboundProducts.value.length > 0
      )

      watch(
        () => bookingStore.bookingData?.booking_number,
        async (newBookingNumber, oldBookingNumber) => {
          if (newBookingNumber) {
            await initializeBookingData()
          }
        },
        { immediate: true }
      )

      return {
        selectedOutboundProducts,
        selectedInboundProducts,
        selectedOutboundProductsWithIds,
        selectedInboundProductsWithIds,
        outboundLegs,
        inboundLegs,
        outboundProducts,
        inboundProducts,
        isLoading,
        hasTransfers,
        isDrawerOpen,
        isMobileView,
        isRebooking,
        myTravelStore,
        origin,
        destination,
        hasReturnTravel,
        hasSelectedProducts,
        hasInboundProducts,
        activeTab,
        chevron,
        addProduct,
        removeProduct,
        submitProductsHandler,
        toggleDrawer: () => {
          isDrawerOpen.value = !isDrawerOpen.value
        },
        handleBack,
      }
    },
  })
</script>

<style lang="scss" scoped>
  @import '@/styles/grid.scss';

  .equipment-page {
    &__form {
      display: flex;
      flex-direction: column;
    }

    &__heading {
      text-align: left;
      margin: 0;
    }

    &__navigation {
      margin-top: 20px;
    }

    .navigation-bar {
      &__lower {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 8px;
      }

      @media screen and (max-width: $breakpoint-sm) {
        &__upper {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 8px;
        }
      }
    }

    .selected-products {
      margin-top: 20px;
      h3 {
        margin-bottom: 10px;
      }
      ul {
        list-style: none;
        padding: 0;
        li {
          margin-bottom: 5px;
        }
      }
    }
  }
</style>
